import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Container, Grid, Divider, IconButton, CircularProgress, useTheme, Breadcrumbs, Link } from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from "../../MiniBot";
import { useAuth } from "../../../helpers/AuthProvider";

const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

const DataScansDetailsPageContent: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState<any>(null);
    const { user } = useAuth();
    const [dynamicLabels, setDynamicLabels] = useState({ projectId: 'Project ID' });

    useEffect(() => {
        const fetch = async () => {
            const res = await queryGraphql(`
                query GetDataScan($id: String!) {
                    getDataScan(id: $id) {
                        id name status cloudProvider solutionOwner { name }
                        details { dataScans { scanType projectId location schedule targetProjectId targetTableId targetDatasetId exportProjectId exportDatasetId exportTableId rules { column dimension } excludeFields samplingPercent description } }
                        reasonForRejection
                    }
                }
            `, { id });
            const scanData = res?.getDataScan || res?.data?.getDataScan;
            if (scanData) setData(scanData);
        };
        fetch();
    }, [id]);

    useEffect(() => {
        if (data?.cloudProvider) {
            switch (data.cloudProvider) {
                case 'aws': setDynamicLabels({ projectId: 'Account ID' }); break;
                case 'azure': setDynamicLabels({ projectId: 'Subscription ID' }); break;
                default: setDynamicLabels({ projectId: 'Project ID' }); break;
            }
        }
    }, [data]);

    if (!data) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px"><CircularProgress size={24} /><Typography sx={{ ml: 2 }}>Loading...</Typography></Box>;

    const ds = data.details?.dataScans || {};
    
    const getStatusBackgroundColor = (status: string | undefined): string => {
        if (!status) return "#F8F8F8";
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved") return "#12B76A10";
        if (lowerStatus === "rejected") return "#F0443810";
        if (lowerStatus === "pending") return "#FDB02210";
        return "#F8F8F8";
    };

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/datasolutions/data-scans" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/datasolutions/data-scans'); }}> Data Scans </Link>
                <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Data Scan Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/datasolutions/data-scans')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Data Scan Details </Typography>
            </Box>

            <Box sx={{mx:0, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{ p: 2.5, bgcolor: getStatusBackgroundColor(data.status), mb: 0, mx: 0, borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>{data.name}</Typography>
                            {user.roles?.some((e: string) => ["cluster_owner"].includes(e)) && data.status === "pending" && (
                                <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/datasolutions/data-scans/edit/${id}`)}>
                                    <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>Resource Owner: {data.solutionOwner?.name}</Typography>
                    </Box>
                </Box>

                <Grid container spacing={3} sx={{p:3, pt: 2.5 }}>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Type" value={ds.scanType} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label={dynamicLabels.projectId} value={ds.projectId} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Schedule" value={ds.schedule} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Location" value={ds.location} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Cloud Provider" value={data.cloudProvider?.toUpperCase()} /></Grid>
                </Grid>
            </Box>

            {data.status === 'rejected' && (
                <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
                    <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                    <Divider sx={{ marginY: 2 }} />
                    <DetailItem label="Reason for Rejection" value={data.reasonForRejection} />
                </Box>
            )}

            {data.status === 'active' && (
                <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
                    <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Scan Specifications</Typography>
                    <Divider sx={{ marginY: 2 }} />
                    <Grid container spacing={2.5}>
                        <Grid item xs={12}><DetailItem label="Description" value={ds.description} /></Grid>
                        <Grid item xs={12} sm={12} md={6}><DetailItem label="Target Table" value={`${ds.targetProjectId}.${ds.targetDatasetId}.${ds.targetTableId}`} /></Grid>
                        <Grid item xs={12} sm={12} md={6}><DetailItem label="Export Table" value={`${ds.exportProjectId}.${ds.exportDatasetId}.${ds.exportTableId}`} /></Grid>
                        
                        {ds.scanType === "Quality" && ds.rules && (
                            <Grid item xs={12}>
                                <Typography fontFamily="Gilroy-Bold" mb={1} fontSize="14px">Rules Defined</Typography>
                                {ds.rules.map((r: any, i: number) => (
                                    <Typography key={i} fontSize="13px" fontFamily="Gilroy-Regular">• {r.column} ({r.dimension})</Typography>
                                ))}
                            </Grid>
                        )}
                        {ds.scanType === "Profile" && (
                            <>
                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Sampling Percent" value={`${ds.samplingPercent}%`} /></Grid>
                                <Grid item xs={12}><DetailItem label="Exclude Fields" value={ds.excludeFields?.join(", ")} /></Grid>
                            </>
                        )}
                    </Grid>
                </Box>
            )}
        </Container>
    );
};

const DataScansDetails: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"datasolutions-data-scans"} onStateChange={handleDrawerStateChange} />
            <MiniBot/>
            <Box component="main" sx={{ flexGrow: 1, p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: {xs: 0, sm: `${currentMarginLeft}px`}, }}>
                <DataScansDetailsPageContent />
            </Box>
        </Box>
    );
};

export default DataScansDetails;